import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState, type DragEvent } from "react";

import { crmApi, customersApi, dealsApi, type CustomerPayload } from "@/api/endpoints";
import type { DealDetail, ImportedRow } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { Note } from "@/components/ui/Note";
import { ActionHint, Panel, PanelActions, PanelHead, Spacer } from "@/components/ui/Panel";
import { Tag } from "@/components/ui/Pill";
import { SearchInput } from "@/components/ui/SearchInput";
import type { MsgKey } from "@/i18n/messages";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";

interface StepProps {
  deal: DealDetail;
  /** Someone else's Vorgang: show it, but offer nothing that writes. */
  readOnly: boolean;
  onChanged: (deal: DealDetail) => void;
  onNext: () => void;
  onBack: () => void;
}

const EMPTY_FORM: CustomerPayload = {
  company_name: "",
  street: "",
  postal_code: "",
  city: "",
  contact_name: "",
  contact_email: "",
};

/** Fields the contract needs; mirrors the backend's own check. */
function missingFields(form: CustomerPayload): string[] {
  const missing: string[] = [];
  if (!form.company_name.trim()) missing.push("company_name");
  if (!form.street.trim()) missing.push("street");
  if (!form.postal_code.trim() && !form.city.trim()) missing.push("postal_city");
  if (!form.contact_name.trim()) missing.push("contact_name");
  if (!form.contact_email.trim()) missing.push("contact_email");
  return missing;
}

/** The field codes the API reports as missing, in the wording the UI uses. */
const FIELD_KEYS: Record<string, MsgKey> = {
  company_name: "field.company_name",
  street: "field.street",
  postal_city: "field.postal_city",
  postal_code: "field.postal_code",
  city: "field.city",
  contact_name: "field.contact_name",
  contact_email: "field.contact_email",
};

const COLUMNS: MsgKey[] = [
  "field.company_name",
  "field.city",
  "field.contact_name",
  "field.contact_email",
];

export function CustomerStep({ deal, readOnly, onChanged, onNext, onBack }: StepProps) {
  const { t } = useI18n();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rows, setRows] = useState<ImportedRow[] | null>(null);
  // Which file the rows on screen came from, so it is never ambiguous.
  const [loadedFile, setLoadedFile] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [form, setForm] = useState<CustomerPayload>(EMPTY_FORM);

  const signatureLocked = deal.signature_request !== null;
  // readOnly folds in for disabling, but not for the warning below: telling a
  // viewer the signature is out would be a different, untrue reason.
  const locked = readOnly || signatureLocked;

  // Listed for the provider names and branding only; connecting is disabled.
  const crm = useQuery({ queryKey: ["crm"], queryFn: crmApi.list });

  const upload = useMutation({
    mutationFn: (file: File) => customersApi.upload(file),
    // Cleared as the request starts, not on success: if the new file fails to
    // parse, leaving the previous list on screen makes the error look like it
    // belongs to rows the user is still looking at.
    onMutate: (file) => {
      setRows(null);
      setLoadedFile(file.name);
      setSelectedRow(null);
      setForm(EMPTY_FORM);
      setSearch("");
    },
    onSuccess: (result) => setRows(result.rows),
    onError: () => setLoadedFile(null),
  });

  /** Discard the imported list and go back to searching stored customers. */
  function clearImport() {
    setRows(null);
    setLoadedFile(null);
    setSelectedRow(null);
    setForm(EMPTY_FORM);
    setSearch("");
    upload.reset();
  }

  const attach = useMutation({
    mutationFn: (payload: CustomerPayload) => dealsApi.createCustomer(deal.id, payload),
    onSuccess: onChanged,
  });

  // Existing customers, so a returning customer needs no re-import.
  const existing = useQuery({
    queryKey: ["customers", search],
    queryFn: () => customersApi.search(search),
    // Only relevant before a file has been loaded.
    enabled: rows === null,
  });

  const attachExisting = useMutation({
    mutationFn: (customerId: string) => dealsApi.selectCustomerById(deal.id, customerId),
    onSuccess: onChanged,
  });

  const visibleRows = useMemo(() => {
    if (!rows) return [];
    const needle = search.trim().toLowerCase();
    const indexed = rows.map((row, index) => ({ row, index }));
    if (!needle) return indexed;
    return indexed.filter(({ row }) =>
      [row.company_name, row.street, row.postal_code, row.city, row.contact_name, row.contact_email]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [rows, search]);

  function handleFile(file: File | undefined) {
    if (file) upload.mutate(file);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    handleFile(event.dataTransfer.files[0]);
  }

  function pickRow(index: number) {
    const row = rows?.[index];
    if (!row) return;
    setSelectedRow(index);
    setForm({
      company_name: row.company_name,
      street: row.street,
      postal_code: row.postal_code,
      city: row.city,
      contact_name: row.contact_name,
      contact_email: row.contact_email,
    });
  }

  /** "Es fehlt: Firma, PLZ und Ort." - empty when nothing is missing. */
  function missingSentence(fields: string[]): string {
    if (fields.length === 0) return "";
    // An unknown code falls back to itself rather than vanishing.
    const names = fields.map((field) => (FIELD_KEYS[field] ? t(FIELD_KEYS[field]) : field));
    return t("field.missingSentence", { fields: names.join(", ") });
  }

  const formMissing = missingFields(form);
  const attached = deal.customer;
  const canContinue = attached !== null && attached.missing_contract_fields.length === 0;

  return (
    <Panel>
      <PanelHead eyebrow={t("customer.eyebrow")} title={t("customer.title")}>
        {t("customer.intro")}
      </PanelHead>

      {signatureLocked && (
        <Note tone="warn" className="mb-4">
          {t("customer.locked")}
        </Note>
      )}

      {attached && (
        <Note tone={canContinue ? "ok" : "warn"} className="mb-4.5">
          <b>{attached.company_name}</b> {t("customer.attached")}{" "}
          {canContinue
            ? t("customer.complete")
            : missingSentence(attached.missing_contract_fields)}
        </Note>
      )}

      {/* Drop zone */}
      <div
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "rounded-card border-2 border-dashed px-5 py-8 text-center transition-colors",
          dragging ? "border-teal bg-teal-wash" : "border-line bg-page",
        )}
      >
        <h3 className="text-[17px] font-bold">{t("customer.dropTitle")}</h3>
        <p className="mx-auto mt-1.5 mb-1 max-w-[56ch] text-[13.5px] text-muted">
          {t("customer.dropBody")}
        </p>
        <p className="mx-auto mb-4 max-w-[56ch] text-[12.5px] text-muted">
          {t("customer.dropHint")}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          <Button
            variant={rows ? "ghost" : "primary"}
            busy={upload.isPending}
            disabled={locked}
            onClick={() => fileInput.current?.click()}
          >
            {t(rows ? "customer.pickOther" : "customer.pickFile")}
          </Button>
          {rows && (
            <Button variant="ghost" disabled={locked} onClick={clearImport}>
              {t("customer.discard")}
            </Button>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.xlsx"
          hidden
          onChange={(event) => {
            handleFile(event.target.files?.[0]);
            // Reset so re-selecting the same filename fires change again.
            event.target.value = "";
          }}
        />

        {loadedFile && !upload.isError && (
          <p className="mt-3.5 text-[12.5px] text-muted">
            <span>{t("customer.loaded")} </span>
            <span className="font-semibold text-ink">{loadedFile}</span>
            {rows
              ? t(rows.length === 1 ? "customer.rowCount.one" : "customer.rowCount.other", {
                  count: rows.length,
                })
              : t("customer.reading")}
          </p>
        )}
      </div>

      {upload.isError && (
        <Note tone="error" className="mt-3.5">
          <b>{t("customer.uploadFailed")}</b> {(upload.error as Error).message}
          <span className="mt-1 block text-[12.5px]">{t("customer.uploadFailedHint")}</span>
        </Note>
      )}

      {/* CRM slots - listed but not connectable yet. Shown rather than hidden
          so the direction is visible; disabled so nothing pretends to work. */}
      <div className="mt-6 flex flex-wrap items-center gap-2.5">
        <h4 className="m-0 text-[14px] font-bold">{t("customer.crmTitle")}</h4>
        <Tag muted>{t("customer.crmSoon")}</Tag>
      </div>
      <p className="m-0 text-[13px] text-muted">{t("customer.crmBody")}</p>
      <div className="mt-3.5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {crm.data?.map((connection) => (
          <div
            key={connection.provider}
            aria-disabled="true"
            className="rounded-card border border-line bg-surface p-3.5 opacity-60"
          >
            <div className="flex items-center gap-2.5">
              <div
                className="grid size-[30px] shrink-0 place-items-center rounded-lg text-[11px] font-bold text-white"
                style={{ background: connection.colour }}
              >
                {connection.initials}
              </div>
              <div className="min-w-0">
                <div className="truncate text-[13.5px] font-semibold">
                  {connection.display_name}
                </div>
                <div className="truncate text-[11.5px] text-muted">
                  {t("customer.crmSoonAvailable")}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="mt-3 w-full" disabled>
              {t("customer.crmSoon")}
            </Button>
          </div>
        ))}
      </div>

      {/* Imported rows, or a search over existing customers */}
      <div className="mt-6.5">
        <h4 className="mb-2.5 text-[14px] font-bold">
          <span>{t(rows ? "customer.rowsTitle" : "customer.storedTitle")}</span>
          <span className="ml-2 text-[12.5px] font-normal text-muted">
            {t(rows ? "customer.rowsHint" : "customer.storedHint")}
          </span>
        </h4>
        <SearchInput
          label={t(rows ? "customer.searchRows" : "customer.searchStored")}
          value={search}
          onChange={setSearch}
          placeholder={t("customer.searchPlaceholder")}
          count={rows ? `${visibleRows.length} / ${rows.length}` : undefined}
          className="max-w-[560px]"
        />

        <div className="mt-3.5 overflow-x-auto rounded-card border border-line">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr className="border-b border-line bg-page text-left">
                {COLUMNS.map((key) => (
                  <th
                    key={key}
                    scope="col"
                    className="px-3.5 py-2.5 text-[11px] font-semibold tracking-[0.08em] text-muted uppercase"
                  >
                    {t(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows === null &&
                existing.data?.map((customer) => (
                  <tr
                    key={customer.id}
                    tabIndex={locked ? undefined : 0}
                    role={locked ? undefined : "button"}
                    onClick={() => !locked && attachExisting.mutate(customer.id)}
                    onKeyDown={(event) => {
                      if (!locked && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        attachExisting.mutate(customer.id);
                      }
                    }}
                    className={cn(
                      "border-b border-line-soft last:border-0",
                      !locked && "cursor-pointer hover:bg-teal-wash/60",
                      attached?.id === customer.id && "bg-teal-wash",
                    )}
                  >
                    <td className="px-3.5 py-2.5 font-semibold">{customer.company_name}</td>
                    <td className="px-3.5 py-2.5">{customer.postal_city || "—"}</td>
                    <td className="px-3.5 py-2.5">
                      {customer.contact_name || <span className="text-warn">{t("common.missing")}</span>}
                    </td>
                    <td className="px-3.5 py-2.5 break-all">
                      {customer.contact_email || <span className="text-warn">{t("common.missing")}</span>}
                    </td>
                  </tr>
                ))}

              {rows !== null &&
                visibleRows.map(({ row, index }) => (
                  <tr
                    key={index}
                    tabIndex={0}
                    role="button"
                    onClick={() => pickRow(index)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        pickRow(index);
                      }
                    }}
                    className={cn(
                      "cursor-pointer border-b border-line-soft last:border-0 hover:bg-teal-wash/60",
                      selectedRow === index && "bg-teal-wash",
                    )}
                  >
                    <td className="px-3.5 py-2.5 font-semibold">{row.company_name || "—"}</td>
                    <td className="px-3.5 py-2.5">
                      {[row.postal_code, row.city].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-3.5 py-2.5">
                      {row.contact_name || <span className="text-warn">{t("common.missing")}</span>}
                    </td>
                    <td className="px-3.5 py-2.5 break-all">
                      {row.contact_email || <span className="text-warn">{t("common.missing")}</span>}
                    </td>
                  </tr>
                ))}

              {((rows === null && existing.data?.length === 0) ||
                (rows !== null && visibleRows.length === 0)) && (
                <tr>
                  <td colSpan={4} className="px-3.5 py-7 text-center text-muted">
                    {t("customer.noMatch")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Correction form for the picked row */}
      {selectedRow !== null && (
        <div className="mt-4.5">
          {formMissing.length > 0 && (
            <Note tone="warn" className="mb-3.5">
              <b>{t("customer.incompleteRow")}</b> {missingSentence(formMissing)}{" "}
              {t("customer.incompleteHint")}
            </Note>
          )}

          <div className="grid gap-3.5 sm:grid-cols-2">
            <Field
              label={t("field.company_name")}
              value={form.company_name}
              missing={!form.company_name.trim()}
              onChange={(event) => setForm({ ...form, company_name: event.target.value })}
            />
            <Field
              label={t("field.contact_name")}
              value={form.contact_name}
              missing={!form.contact_name.trim()}
              onChange={(event) => setForm({ ...form, contact_name: event.target.value })}
            />
            <Field
              label={t("customer.field.street")}
              value={form.street}
              missing={!form.street.trim()}
              onChange={(event) => setForm({ ...form, street: event.target.value })}
            />
            {/* Was grid-cols-[100px_1fr]: the fixed column survived 375px but not 640px,
                where sm:grid-cols-2 halves the parent while 100px stays absolute. */}
            <div className="flex gap-2.5">
              <Field
                label={t("field.postal_code")}
                value={form.postal_code}
                missing={!form.postal_code.trim()}
                onChange={(event) => setForm({ ...form, postal_code: event.target.value })}
                className="w-24 shrink-0"
              />
              <Field
                label={t("field.city")}
                value={form.city}
                missing={!form.city.trim()}
                onChange={(event) => setForm({ ...form, city: event.target.value })}
                className="min-w-0 flex-1"
              />
            </div>
            <Field
              label={t("customer.field.email")}
              type="email"
              value={form.contact_email}
              missing={!form.contact_email.trim()}
              onChange={(event) => setForm({ ...form, contact_email: event.target.value })}
              className="sm:col-span-2"
            />
          </div>

          <Button
            className="mt-4"
            disabled={locked || formMissing.length > 0}
            busy={attach.isPending}
            onClick={() => attach.mutate(form)}
          >
            {/* Named for what it actually does, so replacing an already
                attached customer is not a surprise. */}
            {t(
              attached && attached.company_name !== form.company_name
                ? "customer.replace"
                : "customer.attach",
            )}
          </Button>

          {attach.isError && (
            <Note tone="error" className="mt-3.5">
              {(attach.error as Error).message}
            </Note>
          )}
        </div>
      )}

      {attachExisting.isError && (
        <Note tone="error" className="mt-3.5">
          {(attachExisting.error as Error).message}
        </Note>
      )}

      <PanelActions>
        <Button variant="ghost" onClick={onBack}>
          {t("common.back")}
        </Button>
        <Spacer />
        <ActionHint>
          {t(
            !attached
              ? "customer.hint.none"
              : canContinue
                ? "customer.hint.complete"
                : "customer.hint.incomplete",
          )}
        </ActionHint>
        <Button disabled={!canContinue} onClick={onNext}>
          {t("customer.continue")}
        </Button>
      </PanelActions>
    </Panel>
  );
}
